import React, { useEffect, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useThread } from "scenes/LandingPage/contexts/ThreadContext";
import { useBrandGuidelinesRealtime } from "hooks/useBrandGuidelinesRealtime";

const isEmpty = (g: BrandGuidelines): boolean =>
  (g.colors?.length ?? 0) === 0 &&
  (g.fonts?.length ?? 0) === 0 &&
  !g.voice_and_tone &&
  !g.photography_style &&
  !g.lighting_rules &&
  !g.composition_rules;

const BrandGuidelinesGateModal: React.FC = () => {
  const navigate = useNavigate();
  const { currentProjectId, currentThreadId } = useThread();
  const { guidelines, loading } = useBrandGuidelinesRealtime(currentProjectId);
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    setSkipped(false);
  }, [currentThreadId]);

  if (!currentThreadId) return null;
  if (loading) return null;
  if (!guidelines) return null;
  if (!isEmpty(guidelines)) return null;
  if (skipped) return null;

  const handleSetup = () => {
    if (!currentProjectId) return;
    navigate(generatePath("/projects/:project_id/settings", { project_id: currentProjectId }));
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="brand-gate-title"
    >
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-md shadow-xl">
        <h2 id="brand-gate-title" className="text-xl font-semibold text-gray-100 mb-2">
          Set up brand guidelines
        </h2>
        <p className="text-sm text-gray-400 mb-6">
          Your project does not have any brand guidelines yet. Setting them up helps the AI
          generate images that match your brand's colors, fonts, and visual style.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setSkipped(true)}
            className="text-gray-400 hover:text-gray-200 font-medium py-2 px-4"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={handleSetup}
            className="bg-gradient-to-br from-blue-600 to-cyan-500 text-white font-semibold py-2 px-5 rounded-lg shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/40 transition-all"
          >
            Set up brand
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandGuidelinesGateModal;
